import {
  APIEntityType,
  APIFieldType,
  APIOrganizationUser,
  ArdoqId,
  DiffMode,
  Features,
  FilterInterfaceFilter,
} from '@ardoq/api-types';
import { FieldSource } from '@ardoq/bubble-chart';
import type {
  BubbleChartViewModelSourceData,
  BubbleChartViewSettings,
  ComponentTypeRegistryValue,
} from '@ardoq/bubble-chart';
import {
  INITIAL_SOURCE_DATA_STATE,
  findTypeIdByComponentId,
  getAxisTitlesForComponentChart,
  resolveRadiusField,
  sortDataPointsByCluster,
  bubbleChartOperations,
} from '@ardoq/bubble-chart';
import {
  buildComponentTypeRegistry,
  toComponentDataPointModelWithScopeData,
  isCompliantToSelectedFields,
  getDiffMode,
  createOnShowSizeZeroBubblesHandler,
  getComponentIdsOfTypes,
} from '../util';
import {
  getFieldInstancesForViewpoint,
  toFieldsForViewpoint,
} from 'streams/fields/getFieldsForViewpoint';
import { isTruthy, WILDCARD } from '@ardoq/common-helpers';
import { uniqBy } from 'lodash';
import {
  getLabelWithFormattingForViewsNotSupportingMultilabeling,
  getVisualizationInterface,
  LoadedGraphWithViewpointMode,
  type LabelsInterface,
  type ScopeDataParams,
} from '@ardoq/graph';
import {
  type ContextShape,
  type FormattingAndLabelRules,
  type GraphModelMap,
  type ScopeDiffState,
} from '@ardoq/data-model';
import { linkedComponentToReferenceInfoWithScopeData } from '../referenceBubbleChart/viewModel$';
import type { ReferenceWithBubbleComponent } from '../referenceBubbleChart/types';
import { resetReferenceBubbleChartWithScopeData } from '../referenceBubbleChart/resetReferenceBubbleChartWithScopeData';
import {
  toBubbleChartSources,
  toValueComponentTypeIds,
} from './bubbleChartSources';
import { findReferenceFields, toReferenceTypeGroups } from './sourceOptions';
import {
  BubbleChartSpecificInterface,
  getBubbleChartSpecificInterface,
} from '../bubbleChartSpecificInterface';
import { isPresentationMode } from 'env';
import { openViewSettingsPanel } from 'viewSettings/openViewSettingsPanel';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';

import { ARDOQ_DEFAULT_LOCALE, Locale } from '@ardoq/locale';
import { type I18nLocale } from '@ardoq/i18n';
import {
  getSelectedQuadrantDropdownLabels,
  getTimeLayoutLabels,
} from 'locales/getBubbleChartLabels';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { createBubbleChartPopovers } from '../popovers';
import { getBubbleChartTrackingCallbacks } from '../streams/performanceTrackingUtils';
import { PermissionContext } from '@ardoq/access-control';
import { type FeatureContext, featureOperations } from '@ardoq/features';
import {
  enhancedScopeDataOperations,
  type FilterColorData,
} from '@ardoq/scope-data';
import { type EnhancedScopeData } from '@ardoq/data-model';
import { filterCache } from 'collections/filterCache';
import { warmFilterCacheHack } from 'collections/warmFilterCacheHack';
import { buildReferenceFilterContext } from '@ardoq/filter-helpers';
import { usersOperations } from '@ardoq/core';

type ShouldBeDisplayedArgs = {
  componentId: ArdoqId;
  enhancedScopeData: EnhancedScopeData;
  componentTypeRegistry: Map<string, ComponentTypeRegistryValue>;
  relevantComponentTypeIds: Set<string>;
  xField: string;
  yField: string;
  radiusField: string;
};
const shouldBeDisplayed = ({
  componentId,
  enhancedScopeData,
  componentTypeRegistry,
  relevantComponentTypeIds,
  xField,
  yField,
  radiusField,
}: ShouldBeDisplayedArgs) => {
  // remove components that are not of the selected type(s)
  const typeId = findTypeIdByComponentId(componentId, componentTypeRegistry);
  if (!(typeId && relevantComponentTypeIds.has(typeId))) {
    return false;
  }

  return isCompliantToSelectedFields(
    enhancedScopeData,
    componentId,
    xField,
    yField,
    radiusField
  );
};

type ReferencesData = {
  incomingReferences: ReferenceWithBubbleComponent[];
  outgoingReferences: ReferenceWithBubbleComponent[];
};

const collectReferencesData = (
  scopeData: EnhancedScopeData,
  sourceMap: GraphModelMap,
  targetMap: GraphModelMap,
  scopeComponentIds: ArdoqId[]
): ReferencesData => {
  const collectLinkedComponents = (referenceMap: GraphModelMap) =>
    uniqBy(
      scopeComponentIds.flatMap(bubbleComponentId =>
        (referenceMap.get(bubbleComponentId) ?? []).map(linkedComponent => ({
          ...linkedComponent,
          bubbleComponentId,
        }))
      ),
      'referenceId'
    );

  const [incomingLinkedComponents, outgoingLinkedComponents] = [
    targetMap,
    sourceMap,
  ].map(collectLinkedComponents);

  const [incomingReferences, outgoingReferences] = [
    incomingLinkedComponents,
    outgoingLinkedComponents,
  ].map(linkedComponents =>
    linkedComponents
      .map(linkedComponent =>
        linkedComponentToReferenceInfoWithScopeData(scopeData, linkedComponent)
      )
      .filter(isTruthy)
  );

  return { incomingReferences, outgoingReferences };
};

const EMPTY_REFERENCES_DATA: ReferencesData = {
  incomingReferences: [],
  outgoingReferences: [],
};

type GetViewpointModeSourceDataArgs = {
  enhancedScopeData: EnhancedScopeData;
  permissionContext: PermissionContext;
  context: ContextShape;
  loadedGraph: LoadedGraphWithViewpointMode;
  selectedComponentTypes: Set<string>;
  viewSettings: BubbleChartViewSettings;
  hasReferenceBubbleChartFeature: boolean;
  locale: Locale;
};
const getViewpointModeSourceData = ({
  enhancedScopeData,
  permissionContext,
  context,
  loadedGraph,
  selectedComponentTypes,
  viewSettings,
  hasReferenceBubbleChartFeature,
  locale,
}: GetViewpointModeSourceDataArgs) => {
  const viewpointComponents = loadedGraph.scopeComponentIds;
  const { sourceMap, targetMap } = enhancedScopeData.graphData;

  const componentTypeRegistry = buildComponentTypeRegistry(
    enhancedScopeData,
    viewpointComponents
  );

  const isSelected = (componentTypeId: string) =>
    selectedComponentTypes.has(componentTypeId) ||
    selectedComponentTypes.has(WILDCARD);

  const allTypeIds = Array.from(componentTypeRegistry.keys());
  const allTypeIdsSet = new Set(allTypeIds);

  const selectedTypes = new Set<string>(allTypeIds.filter(isSelected));

  const bubbleTypeIds =
    selectedComponentTypes.has(WILDCARD) || !selectedTypes.size
      ? allTypeIdsSet
      : selectedTypes;

  const { incomingReferences, outgoingReferences } =
    hasReferenceBubbleChartFeature
      ? collectReferencesData(
          enhancedScopeData,
          sourceMap,
          targetMap,
          getComponentIdsOfTypes(componentTypeRegistry, bubbleTypeIds)
        )
      : EMPTY_REFERENCES_DATA;

  const numericFieldInstances = getFieldInstancesForViewpoint({
    scopeData: enhancedScopeData,
    workspaceIds: context.workspaceIds,
    types: [APIFieldType.NUMBER],
  });

  const allNumericFields = toFieldsForViewpoint({
    permissionContext,
    fieldInstances: numericFieldInstances,
    relevantComponentTypeIds: allTypeIdsSet,
  });

  const referenceTypeGroups = hasReferenceBubbleChartFeature
    ? toReferenceTypeGroups({
        incomingReferences,
        outgoingReferences,
        enhancedScopeData,
        permissionContext,
      })
    : [];

  const sources = hasReferenceBubbleChartFeature
    ? toBubbleChartSources({
        viewSettings,
        bubbleTypeIds,
        allNumericFields,
        componentTypeRegistry,
        referenceTypeGroups,
        enhancedScopeData,
        numericFieldInstances,
        locale,
      })
    : null;

  const selectedReferenceFields = sources
    ? findReferenceFields(referenceTypeGroups, sources.valueSource)
    : null;

  const valueComponentTypeIds = sources
    ? toValueComponentTypeIds(sources.valueSource, bubbleTypeIds)
    : bubbleTypeIds;

  const numericFields = selectedReferenceFields
    ? []
    : valueComponentTypeIds === allTypeIdsSet
      ? allNumericFields
      : toFieldsForViewpoint({
          permissionContext,
          fieldInstances: numericFieldInstances,
          relevantComponentTypeIds: valueComponentTypeIds,
        });

  const { selectedFieldNameX, selectedFieldNameY, selectedFieldNameRadius } =
    viewSettings;

  const fieldNamesInScope = new Set(numericFields.map(({ name }) => name));

  const [xField, yField, radiusField] = [
    selectedFieldNameX,
    selectedFieldNameY,
    selectedFieldNameRadius,
  ].map(field =>
    typeof field === 'string' && fieldNamesInScope.has(field) ? field : ''
  );

  const displayedComponents = numericFields.length
    ? viewpointComponents.filter(componentId =>
        shouldBeDisplayed({
          componentId,
          enhancedScopeData,
          componentTypeRegistry,
          relevantComponentTypeIds: valueComponentTypeIds,
          xField,
          yField,
          radiusField,
        })
      )
    : [];

  return {
    components: displayedComponents,
    numericFields,
    componentTypeRegistry,
    allNumericFields,
    xField,
    yField,
    radiusField,
    incomingReferences,
    outgoingReferences,
    sources,
    selectedReferenceFields,
  };
};

type ViewpointModeResetArgs = Pick<
  BubbleChartViewModelSourceData,
  'numericFields' | 'allNumericFields'
> & {
  components: ArdoqId[];
  highlightedComponentId: ArdoqId | null;
  viewSettings: BubbleChartViewSettings;
  componentTypeRegistry: Map<string, ComponentTypeRegistryValue>;
  xField: string;
  yField: string;
  radiusField: string;
  locale: Locale;
  enhancedScopeData: EnhancedScopeData;
  filterColorData: FilterColorData;
  i18nLocale: I18nLocale;
  referenceLabelFilters: FilterInterfaceFilter[];
  labelsInterface: LabelsInterface;
};
const viewpointModeReset = (
  previousState: BubbleChartViewModelSourceData,
  {
    components,
    viewSettings,
    numericFields,
    allNumericFields,
    highlightedComponentId,
    componentTypeRegistry,
    xField,
    yField,
    radiusField,
    locale,
    enhancedScopeData,
    filterColorData,
    i18nLocale,
    referenceLabelFilters,
    labelsInterface,
  }: ViewpointModeResetArgs
): BubbleChartViewModelSourceData => {
  const { showBubblesWithZeroValue: viewSettingsShowBubblesWithZeroValue } =
    viewSettings;

  const showBubblesWithZeroValue =
    viewSettingsShowBubblesWithZeroValue === undefined ||
    viewSettingsShowBubblesWithZeroValue;

  const { xAxisTitle, yAxisTitle } = getAxisTitlesForComponentChart(
    viewSettings,
    numericFields,
    locale
  );

  const labelsData: Map<ArdoqId, string> = new Map(
    components.map(componentId => [
      componentId,
      getLabelWithFormattingForViewsNotSupportingMultilabeling({
        modelId: componentId,
        entityLabel:
          enhancedScopeDataOperations.getComponent(
            enhancedScopeData,
            componentId
          )?.name ?? '',
        entityType: APIEntityType.COMPONENT,
        i18nLocale,
        referenceLabelFilters,
        labelsInterface,
      }),
    ])
  );

  const allComponentsData = components
    .map(componentId =>
      toComponentDataPointModelWithScopeData({
        enhancedScopeData,
        filterColorData,
        highlightedComponent: highlightedComponentId,
        componentId,
        selectedFieldNameX: xField,
        selectedFieldNameY: yField,
        selectedFieldNameRadius: radiusField,
        labelsData,
      })
    )
    .filter(isTruthy);

  const unsortedData = allComponentsData
    .map(bubble => (showBubblesWithZeroValue || bubble.radius !== 0) && bubble)
    .filter(isTruthy);

  const data = sortDataPointsByCluster(unsortedData);

  return {
    ...previousState,
    numericFields,
    availableBubbleComponentFields:
      previousState.availableBubbleComponentFields,
    availableReferenceFields: previousState.availableReferenceFields,
    availableReferencedComponentFields:
      previousState.availableReferencedComponentFields,
    isLoadingData: false,
    xAxisTitle,
    yAxisTitle,
    data,
    viewpointModeAttributes: { componentTypeRegistry },
    viewSettings,
    allNumericFields,
  };
};

export const buildViewpointModeViewModelSourceData = (
  previousState: BubbleChartViewModelSourceData,
  {
    context,
    viewSettings,
    loadedGraph,
    scopeDiffState: { scopeDiff },
    activeDiffMode,
    permissionContext,
    locale,
    featureContext,
    i18nLocale,
    orgUsersById,
    formattingAndLabelRules,
    referenceLabelFilters,
    filterColorData,
    trackingId,
  }: BuildViewpointModeBubbleChartSourceDataArgs
): BubbleChartViewModelSourceData => {
  const enhancedScopeData = loadedGraph.scopeData;
  const hasReferenceBubbleChartFeature = featureOperations.hasFeature(
    featureContext,
    Features.REFERENCE_BUBBLE_CHART_IN_VIEWPOINT_MODE
  );

  /**
   * Ensures backward compatibility when feature flag is disabled.
   * Without the flag, viewpoint mode always uses component-based fields.
   */
  const normalizedViewSettings: BubbleChartViewSettings =
    hasReferenceBubbleChartFeature
      ? viewSettings
      : { ...viewSettings, fieldSource: FieldSource.BY_COMPONENT };

  const { selectedComponentTypes: viewSettingsSelectedComponentTypes } =
    normalizedViewSettings;

  const selectedComponentTypes: Set<string> = new Set(
    viewSettingsSelectedComponentTypes
  );

  const {
    components,
    numericFields,
    allNumericFields,
    componentTypeRegistry,
    xField,
    yField,
    radiusField,
    incomingReferences,
    outgoingReferences,
    sources,
    selectedReferenceFields,
  } = getViewpointModeSourceData({
    enhancedScopeData,
    permissionContext,
    context,
    loadedGraph,
    selectedComponentTypes,
    viewSettings: normalizedViewSettings,
    hasReferenceBubbleChartFeature,
    locale,
  });

  const scopeDataParams: ScopeDataParams = {
    enhancedScopeData,
    formattingFilters: formattingAndLabelRules.rules,
    groupingRules: [],
    i18nLocale,
    formatUserFieldFn: val =>
      usersOperations.formatUserField(orgUsersById, val),
  };

  const viewInterface = {
    onViewSettingsUpdate,
    contextMenuName: OBJECT_CONTEXT_MENU_NAME,
    ...getVisualizationInterface<BubbleChartSpecificInterface>({
      scopeDataParams,
      getViewSpecificInterface: getBubbleChartSpecificInterface,
    }),
  };

  const componentModeViewSettings: BubbleChartViewSettings =
    normalizedViewSettings.fieldSource === FieldSource.BY_REFERENCE
      ? { ...normalizedViewSettings, fieldSource: FieldSource.BY_COMPONENT }
      : normalizedViewSettings;

  const newData = selectedReferenceFields
    ? resetReferenceBubbleChartWithScopeData(previousState, {
        viewSettings: normalizedViewSettings,
        numericFields: selectedReferenceFields.numericFields,
        availableIncomingReferenceTypeNames: [],
        availableOutgoingReferenceTypeNames: [],
        incomingReferences,
        outgoingReferences,
        viewpointModeAttributes: {
          componentTypeRegistry,
        },
        availableBubbleComponentFields:
          selectedReferenceFields.availableBubbleComponentFields,
        availableReferenceFields:
          selectedReferenceFields.availableReferenceFields,
        availableReferencedComponentFields:
          selectedReferenceFields.availableReferencedComponentFields,
        hasReferenceBubbleChartFeature,
        enhancedScopeData,
        filterColorData,
        i18nLocale,
        referenceLabelFilters,
        labelsInterface: viewInterface,
      })
    : viewpointModeReset(previousState, {
        components,
        viewSettings: componentModeViewSettings,
        numericFields,
        allNumericFields,
        highlightedComponentId: context.componentId,
        componentTypeRegistry,
        xField,
        yField,
        radiusField,
        locale,
        enhancedScopeData,
        filterColorData,
        i18nLocale,
        referenceLabelFilters,
        labelsInterface: viewInterface,
      });

  const hasOnlyPointsWithZeroRadius = Boolean(newData.isFilteredByRadius);

  /**
   * In reference mode, we use numericFields directly because they already include
   * all available reference fields (not filtered by component type).
   * In component mode, we use allNumericFields to show all possible fields
   * regardless of selected component types for empty state suggestions.
   */
  const allNumericFieldsForEmptyState = selectedReferenceFields
    ? newData.numericFields
    : newData.allNumericFields;

  const emptyStateData = bubbleChartOperations.getEmptyStateData({
    numericFields: newData.numericFields,
    allNumericFields: allNumericFieldsForEmptyState,
    data: newData.data,
    viewSettings: newData.viewSettings,
    isPresentationMode: previousState.isPresentationMode,
    hasOnlyPointsWithZeroRadius,
    isViewLoading: !context.workspaceId,
  });

  const onChangeFieldsClick = emptyStateData.hasFieldsSelected
    ? openViewSettingsPanel
    : undefined;

  const onChangeBubbleSizeField = emptyStateData.hasOnlyPointsWithZeroRadius
    ? openViewSettingsPanel
    : undefined;

  const onShowSizeZeroBubbles = emptyStateData.hasOnlyPointsWithZeroRadius
    ? createOnShowSizeZeroBubblesHandler('bubble')
    : undefined;

  const viewpointEmptyStateData = {
    ...emptyStateData,
    hasSelectedComponentType: selectedReferenceFields
      ? undefined
      : selectedComponentTypes.size > 0 &&
        !selectedComponentTypes.has(WILDCARD),
    onChangeFieldsClick,
    onChangeBubbleSizeField,
    onShowSizeZeroBubbles,
  };

  const { isScopeDiffMode, activeDiffMode: resolvedActiveDiffMode } =
    getDiffMode(scopeDiff, activeDiffMode);

  const trackingCallbacks = trackingId
    ? getBubbleChartTrackingCallbacks(trackingId, newData.data)
    : undefined;

  return {
    ...newData,
    viewInterface: {
      ...viewInterface,
      trackingCallbacks,
    },
    emptyStateData: viewpointEmptyStateData,
    isScopeDiffMode,
    activeDiffMode: resolvedActiveDiffMode,
    locale,
    i18nLocale,
    selectedQuadrantDropdownLabels:
      getSelectedQuadrantDropdownLabels(i18nLocale),
    timeLayoutLabels: getTimeLayoutLabels(i18nLocale),
    popovers: createBubbleChartPopovers(i18nLocale),
    radiusFieldData: resolveRadiusField(
      newData.numericFields,
      newData.viewSettings.selectedFieldNameRadius
    ),
    users: enhancedScopeData.organizationUsers,
    hasReferenceBubbleChartFeature,
    sources,
  };
};

export const warmConditionalFormattingFilterCache = (
  loadedGraph: LoadedGraphWithViewpointMode
): FilterColorData => {
  const enhancedScopeData = loadedGraph.scopeData;
  warmFilterCacheHack({
    scopeData: enhancedScopeData,
    scopeSource: loadedGraph,
    graphModel: enhancedScopeData.graphData,
    referenceFilterContext: buildReferenceFilterContext(enhancedScopeData),
  });
  return filterCache.getFilterColorData();
};

export type ViewpointModeBubbleChartSourceDataArgs = {
  context: ContextShape;
  viewSettings: BubbleChartViewSettings;
  loadedGraph: LoadedGraphWithViewpointMode;
  scopeDiffState: ScopeDiffState;
  activeDiffMode: DiffMode;
  permissionContext: PermissionContext;
  locale: Locale;
  featureContext: FeatureContext;
  i18nLocale: I18nLocale;
  orgUsersById: Record<ArdoqId, APIOrganizationUser>;
  formattingAndLabelRules: FormattingAndLabelRules;
  referenceLabelFilters: FilterInterfaceFilter[];
  /** Duration tracking session started when the source data args were emitted. */
  trackingId?: string;
};

export type BuildViewpointModeBubbleChartSourceDataArgs =
  ViewpointModeBubbleChartSourceDataArgs & {
    filterColorData: FilterColorData;
  };

export const getInitialViewpointModeSourceDataState = (
  viewInstanceId: string
): BubbleChartViewModelSourceData => ({
  ...INITIAL_SOURCE_DATA_STATE,
  viewInstanceId,
  viewInterface: {
    onViewSettingsUpdate,
    contextMenuName: OBJECT_CONTEXT_MENU_NAME,

    ...getVisualizationInterface<BubbleChartSpecificInterface>({
      getViewSpecificInterface: getBubbleChartSpecificInterface,
    }),
  },
  viewpointModeAttributes: {
    componentTypeRegistry: new Map(),
  },
  isPresentationMode: isPresentationMode(),
  locale: ARDOQ_DEFAULT_LOCALE,
});
